import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Users, Calendar, Download, Loader2, 
  Trophy, BookOpen, Users2, DollarSign, Eye, Home, CheckCircle, XCircle, Clock, Printer, Check, Key, Copy
} from 'lucide-react';
import axios from 'axios';
import { ImageUploader } from '../components/ImageUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to parse date without timezone issues
const formatEventDate = (dateStr, options = {}) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    ...options
  });
};

const eventTypeConfig = {
  show: { icon: Trophy, label: 'Horse Show' },
  clinic: { icon: BookOpen, label: 'Clinic' },
  meetup: { icon: Users2, label: 'Meetup' },
};

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, token, getAuthHeader } = useAuth();
  const [events, setEvents] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [pendingChanges, setPendingChanges] = useState([]);
  const [passwordResets, setPasswordResets] = useState([]);
  const [homepageContent, setHomepageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showRegistrationsModal, setShowRegistrationsModal] = useState(false);
  const [showHomepageModal, setShowHomepageModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showEditRegModal, setShowEditRegModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventRegistrations, setEventRegistrations] = useState([]);
  const [editingRegistration, setEditingRegistration] = useState(null);
  const [regFormData, setRegFormData] = useState({
    participant_name: '',
    horse_name: '',
    phone_number: '',
    age_category: '',
    selected_classes: []
  });
  const [printFilter, setPrintFilter] = useState({ ageCategory: 'all', paymentStatus: 'all' });
  const [printView, setPrintView] = useState('table'); // 'table', 'byClass', or 'byAge'
  const [editingEvent, setEditingEvent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'show',
    date: '',
    time: '',
    location: '',
    price: '',
    max_participants: '',
    image_url: '',
    show_classes: '',
    age_categories: '',
    registration_deadline: ''
  });
  const [homepageForm, setHomepageForm] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_image: '',
    about_title: '',
    about_text: '',
    about_image: '',
    cta_title: '',
    cta_text: ''
  });

  const isPresident = user?.role === 'president';
  const isCommittee = user?.role === 'committee';

  useEffect(() => {
    if (!user || !['committee', 'president'].includes(user.role)) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/events`, { headers: getAuthHeader() }),
        axios.get(`${API}/registrations/all`, { headers: getAuthHeader() }),
        axios.get(`${API}/homepage-content`, { headers: getAuthHeader() }),
        axios.get(`${API}/pending-changes?status=pending`, { headers: getAuthHeader() }),
        axios.get(`${API}/auth/password-resets`, { headers: getAuthHeader() })
      ];
      
      const [eventsRes, regsRes, contentRes, changesRes, resetsRes] = await Promise.all(requests);
      setEvents(eventsRes.data);
      setRegistrations(regsRes.data);
      setHomepageContent(contentRes.data);
      setPendingChanges(changesRes.data);
      setPasswordResets(resetsRes.data);
      
      // Initialize homepage form
      if (contentRes.data) {
        setHomepageForm({
          hero_title: contentRes.data.hero_title || '',
          hero_subtitle: contentRes.data.hero_subtitle || '',
          hero_image: contentRes.data.hero_image || '',
          about_title: contentRes.data.about_title || '',
          about_text: contentRes.data.about_text || '',
          about_image: contentRes.data.about_image || '',
          cta_title: contentRes.data.cta_title || '',
          cta_text: contentRes.data.cta_text || ''
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleHomepageChange = (e) => {
    setHomepageForm({ ...homepageForm, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      event_type: 'show',
      date: '',
      time: '',
      location: '',
      price: '',
      max_participants: '',
      image_url: '',
      show_classes: '',
      age_categories: '',
      registration_deadline: ''
    });
    setEditingEvent(null);
  };

  const toLocalDatetimeInput = (iso) => {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openEditModal = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      event_type: event.event_type,
      date: event.date,
      time: event.time,
      location: event.location,
      price: event.price.toString(),
      max_participants: event.max_participants.toString(),
      image_url: event.image_url || '',
      show_classes: event.show_classes?.join(', ') || '',
      age_categories: event.age_categories?.join(', ') || '',
      registration_deadline: event.registration_deadline ? toLocalDatetimeInput(event.registration_deadline) : ''
    });
    setShowEventModal(true);
  };

  const handleSubmitEvent = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.date || !formData.time || !formData.location || !formData.max_participants) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.price) {
      toast.error('Please enter a price');
      return;
    }

    setSubmitting(true);
    try {
      const eventData = {
        title: formData.title,
        description: formData.description,
        event_type: formData.event_type,
        date: formData.date,
        time: formData.time,
        location: formData.location,
        price: parseFloat(formData.price),
        max_participants: parseInt(formData.max_participants),
        image_url: formData.image_url || null
      };

      if (isPresident) {
        eventData.registration_deadline = formData.registration_deadline
          ? new Date(formData.registration_deadline).toISOString()
          : '';
      }

      if (formData.event_type === 'show') {
        const classes = formData.show_classes.trim() 
          ? formData.show_classes.split(',').map(c => c.trim()).filter(c => c)
          : null;
        const categories = formData.age_categories.trim()
          ? formData.age_categories.split(',').map(c => c.trim()).filter(c => c)
          : null;
        
        if (classes && classes.length > 0) {
          eventData.show_classes = classes;
        }
        if (categories && categories.length > 0) {
          eventData.age_categories = categories;
        }
      }

      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, eventData, { headers: getAuthHeader() });
        toast.success('Event updated successfully');
      } else {
        await axios.post(`${API}/events`, eventData, { headers: getAuthHeader() });
        toast.success('Event created successfully');
      }
      
      setShowEventModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event? This will also delete all registrations.')) {
      return;
    }

    try {
      await axios.delete(`${API}/events/${eventId}`, { headers: getAuthHeader() });
      toast.success('Event deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const viewEventRegistrations = async (event) => {
    setSelectedEvent(event);
    try {
      const response = await axios.get(`${API}/registrations/event/${event.id}`, { headers: getAuthHeader() });
      setEventRegistrations(response.data);
      setShowRegistrationsModal(true);
    } catch (error) {
      toast.error('Failed to load registrations');
    }
  };

  // Admin Edit Registration
  const openEditRegistrationModal = (reg) => {
    setEditingRegistration(reg);
    setRegFormData({
      participant_name: reg.participant_name || '',
      horse_name: reg.horse_name || '',
      phone_number: reg.phone_number || '',
      age_category: reg.age_category || '',
      selected_classes: reg.selected_classes || []
    });
    setShowEditRegModal(true);
  };

  const handleRegClassToggle = (className) => {
    setRegFormData(prev => {
      const classes = prev.selected_classes || [];
      if (classes.includes(className)) {
        return { ...prev, selected_classes: classes.filter(c => c !== className) };
      } else {
        return { ...prev, selected_classes: [...classes, className] };
      }
    });
  };

  const handleSaveRegistration = async () => {
    if (!editingRegistration) return;

    if (!regFormData.participant_name.trim()) {
      toast.error('Participant name is required');
      return;
    }
    if (!regFormData.horse_name.trim()) {
      toast.error('Horse name is required');
      return;
    }

    setSubmitting(true);
    try {
      const updateData = {
        participant_name: regFormData.participant_name,
        horse_name: regFormData.horse_name,
        phone_number: regFormData.phone_number
      };

      // Include show-specific fields for horse shows
      if (selectedEvent?.event_type === 'show') {
        updateData.age_category = regFormData.age_category;
        updateData.selected_classes = regFormData.selected_classes;
      }

      await axios.put(`${API}/registrations/${editingRegistration.id}/admin`, updateData, {
        headers: getAuthHeader()
      });

      toast.success('Registration updated');
      setShowEditRegModal(false);
      // Refresh the registrations list
      viewEventRegistrations(selectedEvent);
    } catch (error) {
      console.error('Error updating registration:', error);
      toast.error(error.response?.data?.detail || 'Failed to update registration');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRegistration = async (regId) => {
    if (!window.confirm('Are you sure you want to delete this registration? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/registrations/${regId}/admin`, {
        headers: getAuthHeader()
      });
      toast.success('Registration deleted');
      // Refresh the registrations list
      viewEventRegistrations(selectedEvent);
      // Refresh stats
      fetchData();
    } catch (error) {
      console.error('Error deleting registration:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete registration');
    }
  };

  const handleMarkAsPaid = async (regId) => {
    try {
      await axios.post(`${API}/registrations/${regId}/mark-paid`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Registration marked as paid');
      // Refresh the registrations list
      viewEventRegistrations(selectedEvent);
      // Refresh stats
      fetchData();
    } catch (error) {
      console.error('Error marking as paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark as paid');
    }
  };

  const exportRegistrations = async (eventId) => {
    try {
      const response = await axios.get(`${API}/registrations/export/${eventId}`, { headers: getAuthHeader() });
      const { event_title, registrations: data } = response.data;
      
      if (data.length === 0) {
        toast.error('No registrations to export');
        return;
      }

      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${event_title.replace(/[^a-z0-9]/gi, '_')}_registrations.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export registrations');
    }
  };

  const handleSaveHomepage = async () => {
    setSubmitting(true);
    try {
      const response = await axios.put(`${API}/homepage-content`, homepageForm, { headers: getAuthHeader() });
      toast.success(response.data.message);
      setShowHomepageModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save homepage');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproval = async (changeId, action) => {
    try {
      await axios.post(`${API}/pending-changes/${changeId}/review`, {
        action: action,
        notes: null
      }, { headers: getAuthHeader() });
      toast.success(`Change ${action}d successfully`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action} change`);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Paid</Badge>;
      case 'cash':
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Cash</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'paid': return 'Paid';
      case 'cash': return 'Cash';
      case 'pending': return 'Pending';
      default: return status;
    }
  };

  const openPrintMasterList = async (event) => {
    setSelectedEvent(event);
    setPrintFilter({ ageCategory: 'all', paymentStatus: 'all' });
    setPrintView('table');
    try {
      const response = await axios.get(`${API}/registrations/event/${event.id}`, { headers: getAuthHeader() });
      setEventRegistrations(response.data);
      setShowPrintModal(true);
    } catch (error) {
      toast.error('Failed to load registrations');
    }
  };

  // Get unique age categories from registrations
  const getUniqueAgeCategories = () => {
    const categories = new Set();
    eventRegistrations.forEach(reg => {
      if (reg.age_category) categories.add(reg.age_category);
    });
    return Array.from(categories).sort();
  };

  // Get unique classes from event
  const getEventClasses = () => {
    return selectedEvent?.show_classes || [];
  };

  // Organize registrations by class and age group
  // Filter registrations based on selected filters
  const getFilteredRegistrations = () => {
    return eventRegistrations.filter(reg => {
      const matchesAge = printFilter.ageCategory === 'all' || reg.age_category === printFilter.ageCategory;
      const matchesStatus = printFilter.paymentStatus === 'all' || reg.payment_status === printFilter.paymentStatus;
      return matchesAge && matchesStatus;
    });
  };

  const filteredRegistrations = getFilteredRegistrations();

  // Helper to get all horse names from a registration
  const getHorseNames = (reg) => {
    if (reg.horses && reg.horses.length > 0) {
      return reg.horses.map(h => h.horse_name).filter(name => name).join(', ');
    }
    return reg.horse_name || '-';
  };

  // Helper to flatten registrations so each horse is a separate entry
  const getFlattenedRegistrations = (regs) => {
    const flattened = [];
    regs.forEach(reg => {
      if (reg.horses && reg.horses.length > 0) {
        // Create a separate entry for each horse
        reg.horses.forEach(horse => {
          if (horse.horse_name) {
            flattened.push({
              ...reg,
              horse_name: horse.horse_name,
              horse_classes: horse.selected_classes || [],
              // Use horse's age category if available, otherwise fall back to registration's
              age_category: horse.age_category || reg.age_category,
            });
          }
        });
      } else {
        // Legacy single horse registration
        flattened.push({
          ...reg,
          horse_classes: reg.selected_classes || []
        });
      }
    });
    return flattened;
  };

  // Get flattened registrations for print view (each horse = separate row)
  const flattenedRegistrations = getFlattenedRegistrations(filteredRegistrations);

  const getRegistrationsByClass = () => {
    const classes = getEventClasses();
    const byClass = {};
    
    classes.forEach(className => {
      byClass[className] = {};
      
      // Get all flattened entries where the horse is in this class
      const classEntries = flattenedRegistrations.filter(entry => 
        entry.horse_classes?.includes(className)
      );
      
      // Group by age category
      classEntries.forEach(entry => {
        const ageCategory = entry.age_category || 'Unspecified';
        if (!byClass[className][ageCategory]) {
          byClass[className][ageCategory] = [];
        }
        byClass[className][ageCategory].push(entry);
      });
    });
    
    return byClass;
  };

  // Organize registrations by Age Group first, then by Class
  // Each horse is a separate entry
  const getRegistrationsByAgeGroup = () => {
    const classes = getEventClasses();
    const ageCategories = getUniqueAgeCategories();
    const byAge = {};
    
    ageCategories.forEach(ageCategory => {
      byAge[ageCategory] = {};
      
      classes.forEach(className => {
        // Get flattened entries for this age category where the horse is in this class
        const entries = flattenedRegistrations.filter(entry => 
          entry.age_category === ageCategory && 
          entry.horse_classes?.includes(className)
        );
        
        if (entries.length > 0) {
          byAge[ageCategory][className] = entries;
        }
      });
    });
    
    // Handle unspecified age category
    const unspecifiedEntries = flattenedRegistrations.filter(entry => !entry.age_category);
    if (unspecifiedEntries.length > 0) {
      byAge['Unspecified'] = {};
      classes.forEach(className => {
        const entries = unspecifiedEntries.filter(entry => 
          entry.horse_classes?.includes(className)
        );
        if (entries.length > 0) {
          byAge['Unspecified'][className] = entries;
        }
      });
    }
    
    return byAge;
  };

  const registrationsByClass = getRegistrationsByClass();
  const registrationsByAge = getRegistrationsByAgeGroup();

  const handlePrint = () => {
    const printContent = document.getElementById('print-master-list');
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Master List - ${selectedEvent?.title}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: Arial, sans-serif; 
              padding: 20px; 
              max-width: 1200px; 
              margin: 0 auto;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px;
              border-bottom: 2px solid #1a5f4a;
              padding-bottom: 20px;
            }
            .header h1 { 
              font-size: 24px; 
              margin-bottom: 5px;
              color: #1a5f4a;
            }
            .header h2 { 
              font-size: 18px; 
              font-weight: normal;
              color: #333;
            }
            .header .event-info {
              margin-top: 10px;
              font-size: 14px;
              color: #666;
            }
            .summary {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              padding: 10px;
              background: #f5f5f5;
              border-radius: 4px;
            }
            .summary-item {
              text-align: center;
            }
            .summary-item .label {
              font-size: 12px;
              color: #666;
            }
            .summary-item .value {
              font-size: 18px;
              font-weight: bold;
              color: #1a5f4a;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 10px;
              font-size: 12px;
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 8px; 
              text-align: left; 
            }
            th { 
              background-color: #1a5f4a; 
              color: white;
              font-weight: bold;
            }
            tr:nth-child(even) { background-color: #f9f9f9; }
            tr:hover { background-color: #f5f5f5; }
            .status-paid { color: #16a34a; font-weight: bold; }
            .status-cash { color: #2563eb; font-weight: bold; }
            .status-pending { color: #d97706; font-weight: bold; }
            .classes-list { 
              font-size: 11px; 
              max-width: 200px;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 11px;
              color: #999;
              border-top: 1px solid #ddd;
              padding-top: 10px;
            }
            @media print {
              body { padding: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Stats
  const totalEvents = events.length;
  const totalRegistrations = registrations.length;
  const paidRegistrations = registrations.filter(r => r.payment_status === 'paid').length;
  const totalRevenue = registrations
    .filter(r => r.payment_status === 'paid')
    .reduce((sum, r) => sum + (r.total_price || 0), 0);
  const pendingApprovals = pendingChanges.filter(c => c.status === 'pending').length;

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <div className="bg-secondary py-8 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-display text-3xl font-bold text-stone-900">
                  {isPresident ? 'President' : 'Committee'} Dashboard
                </h1>
                <Badge className={isPresident ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
                  {user?.role}
                </Badge>
              </div>
              <p className="text-stone-600 mt-1">
                {isPresident ? 'Full admin access - manage events and approve changes' : 'Manage events (changes require president approval)'}
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => setShowHomepageModal(true)}
                data-testid="edit-homepage-btn"
              >
                <Home className="w-4 h-4 mr-2" />
                Edit Homepage
              </Button>
              <Button 
                className="bg-terracotta hover:bg-terracotta-light"
                onClick={() => { resetForm(); setShowEventModal(true); }}
                data-testid="create-event-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Event
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="border-stone-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Total Events</p>
                  <p className="text-2xl font-bold text-stone-900">{totalEvents}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-stone-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Registrations</p>
                  <p className="text-2xl font-bold text-stone-900">{totalRegistrations}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-stone-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Confirmed</p>
                  <p className="text-2xl font-bold text-stone-900">{paidRegistrations}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-stone-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-stone-500">Revenue (CAD)</p>
                  <p className="text-2xl font-bold text-stone-900">${totalRevenue.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          {isPresident && (
            <Card className="border-stone-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-500">Pending Approvals</p>
                    <p className="text-2xl font-bold text-stone-900">{pendingApprovals}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Password Resets Card */}
          {passwordResets.length > 0 && (
            <Card className="border-stone-200 border-orange-200 bg-orange-50">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                    <Key className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-500">Password Reset Requests</p>
                    <p className="text-2xl font-bold text-stone-900">{passwordResets.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue={isPresident && pendingApprovals > 0 ? "approvals" : "events"}>
          <TabsList className="mb-6">
            <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
            <TabsTrigger value="registrations" data-testid="tab-registrations">All Registrations</TabsTrigger>
            {passwordResets.length > 0 && (
              <TabsTrigger value="password-resets" data-testid="tab-password-resets" className="relative">
                Password Resets
                <span className="ml-2 bg-orange-600 text-white text-xs rounded-full px-2 py-0.5">
                  {passwordResets.length}
                </span>
              </TabsTrigger>
            )}
            {isPresident && (
              <TabsTrigger value="approvals" data-testid="tab-approvals" className="relative">
                Pending Approvals
                {pendingApprovals > 0 && (
                  <span className="ml-2 bg-purple-600 text-white text-xs rounded-full px-2 py-0.5">
                    {pendingApprovals}
                  </span>
                )}
              </TabsTrigger>
            )}
            {isCommittee && (
              <TabsTrigger value="my-changes" data-testid="tab-my-changes">
                My Submissions
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="events">
            <Card className="border-stone-200">
              <CardHeader>
                <CardTitle className="font-display text-xl">Events</CardTitle>
              </CardHeader>
              <CardContent>
                {events.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                    <p className="text-stone-500">No events yet. Create your first event!</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Event</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead>Classes</TableHead>
                          <TableHead>Registrations</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {events.map((event) => {
                          const config = eventTypeConfig[event.event_type];
                          const Icon = config?.icon || Trophy;
                          const isShow = event.event_type === 'show';
                          return (
                            <TableRow key={event.id} data-testid={`event-row-${event.id}`}>
                              <TableCell className="font-medium">{event.title}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1 text-sm">
                                  <Icon className="w-4 h-4" />
                                  {config?.label || event.event_type}
                                </div>
                              </TableCell>
                              <TableCell>{formatEventDate(event.date)}</TableCell>
                              <TableCell className="font-mono">
                                {isShow ? `$${event.price}/class` : `$${event.price}`}
                              </TableCell>
                              <TableCell>
                                {isShow ? (
                                  <span className="text-sm text-stone-600">
                                    {event.show_classes?.length || 0} classes
                                  </span>
                                ) : '-'}
                              </TableCell>
                              <TableCell>
                                {event.current_participants}/{event.max_participants}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => viewEventRegistrations(event)}
                                    data-testid={`view-regs-btn-${event.id}`}
                                    title="View Registrations"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openPrintMasterList(event)}
                                    data-testid={`print-btn-${event.id}`}
                                    title="Print Master List"
                                  >
                                    <Printer className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => exportRegistrations(event.id)}
                                    data-testid={`export-btn-${event.id}`}
                                    title="Export CSV"
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditModal(event)}
                                    data-testid={`edit-btn-${event.id}`}
                                    title="Edit Event"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-destructive hover:text-destructive"
                                    onClick={() => handleDeleteEvent(event.id)}
                                    data-testid={`delete-btn-${event.id}`}
                                    title="Delete Event"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="registrations">
            <Card className="border-stone-200">
              <CardHeader>
                <CardTitle className="font-display text-xl">All Registrations</CardTitle>
              </CardHeader>
              <CardContent>
                {registrations.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                    <p className="text-stone-500">No registrations yet.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Event</TableHead>
                          <TableHead>Participant</TableHead>
                          <TableHead>Horse</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Total</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {registrations.map((reg) => (
                          <TableRow key={reg.id}>
                            <TableCell className="font-medium">{reg.event_title}</TableCell>
                            <TableCell>{reg.participant_name}</TableCell>
                            <TableCell>{reg.horse_name}</TableCell>
                            <TableCell className="font-mono text-sm">{reg.phone_number}</TableCell>
                            <TableCell className="font-mono">${(reg.total_price || 0).toFixed(2)}</TableCell>
                            <TableCell>{getStatusBadge(reg.payment_status)}</TableCell>
                            <TableCell className="text-stone-500 text-sm">
                              {new Date(reg.created_at).toLocaleDateString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Password Resets Tab */}
          <TabsContent value="password-resets">
            <Card className="border-stone-200">
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <Key className="w-5 h-5 text-orange-600" />
                  Password Reset Requests
                </CardTitle>
              </CardHeader>
              <CardContent>
                {passwordResets.length === 0 ? (
                  <div className="text-center py-12">
                    <Key className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                    <p className="text-stone-500">No pending password reset requests.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-orange-800">
                        <strong>How to help members reset their password:</strong> Copy the reset link below and send it to the member via email, text, or phone.
                      </p>
                    </div>
                    {passwordResets.map((reset) => (
                      <Card key={reset.id} className="border-orange-200 bg-orange-50/50">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-medium text-stone-900">{reset.name}</span>
                                <span className="text-sm text-stone-500">({reset.email})</span>
                              </div>
                              <p className="text-sm text-stone-500">
                                Requested {new Date(reset.created_at).toLocaleString()}
                              </p>
                              <p className="text-sm text-stone-500">
                                Expires {new Date(reset.expires_at).toLocaleString()}
                              </p>
                              <div className="mt-3 p-3 bg-white rounded border border-stone-200">
                                <p className="text-xs text-stone-500 mb-2">Reset Link (send this to the member):</p>
                                <div className="flex items-center gap-2">
                                  <code className="text-sm bg-stone-100 px-2 py-1 rounded flex-1 overflow-x-auto">
                                    {window.location.origin}/reset-password?token={reset.token}
                                  </code>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      navigator.clipboard.writeText(`${window.location.origin}/reset-password?token=${reset.token}`);
                                      toast.success('Reset link copied to clipboard!');
                                    }}
                                  >
                                    <Copy className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {isPresident && (
            <TabsContent value="approvals">
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="font-display text-xl">Pending Approvals</CardTitle>
                </CardHeader>
                <CardContent>
                  {pendingChanges.filter(c => c.status === 'pending').length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                      <p className="text-stone-500">No pending approvals.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingChanges.filter(c => c.status === 'pending').map((change) => (
                        <Card key={change.id} className="border-purple-200 bg-purple-50/50">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge variant="outline" className="capitalize">{change.change_type}</Badge>
                                  <span className="text-sm text-stone-500">
                                    by {change.submitted_by_name}
                                  </span>
                                </div>
                                <p className="font-medium text-stone-900">{change.description}</p>
                                <p className="text-sm text-stone-500 mt-1">
                                  Submitted {new Date(change.submitted_at).toLocaleString()}
                                </p>
                                {change.change_type === 'homepage' && (
                                  <div className="mt-3 p-3 bg-white rounded border border-stone-200">
                                    <p className="text-xs text-stone-500 mb-2">Proposed changes:</p>
                                    {Object.entries(change.changes).map(([key, value]) => (
                                      <div key={key} className="text-sm mb-1">
                                        <span className="font-medium">{key.replace(/_/g, ' ')}:</span>{' '}
                                        <span className="text-stone-600">{String(value).substring(0, 100)}...</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleApproval(change.id, 'approve')}
                                  data-testid={`approve-btn-${change.id}`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleApproval(change.id, 'reject')}
                                  data-testid={`reject-btn-${change.id}`}
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {isCommittee && (
            <TabsContent value="my-changes">
              <Card className="border-stone-200">
                <CardHeader>
                  <CardTitle className="font-display text-xl">My Submissions</CardTitle>
                </CardHeader>
                <CardContent>
                  {pendingChanges.length === 0 ? (
                    <div className="text-center py-12">
                      <Clock className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                      <p className="text-stone-500">No submissions yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingChanges.map((change) => (
                        <Card key={change.id} className="border-stone-200">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge variant="outline" className="capitalize">{change.change_type}</Badge>
                                  {getStatusBadge(change.status)}
                                </div>
                                <p className="font-medium text-stone-900">{change.description}</p>
                                <p className="text-sm text-stone-500 mt-1">
                                  Submitted {new Date(change.submitted_at).toLocaleString()}
                                </p>
                                {change.reviewed_at && (
                                  <p className="text-sm text-stone-500">
                                    Reviewed {new Date(change.reviewed_at).toLocaleString()}
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Create/Edit Event Modal */}
      <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editingEvent ? 'Edit Event' : 'Create New Event'}
            </DialogTitle>
            <DialogDescription>
              {editingEvent ? 'Update event details below' : 'Fill in the event details below'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitEvent} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="title">Event Title *</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Spring Horse Show"
                className="bg-stone-50"
                data-testid="event-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="event_type">Event Type *</Label>
              <Select value={formData.event_type} onValueChange={(v) => setFormData({ ...formData, event_type: v })}>
                <SelectTrigger className="bg-stone-50" data-testid="event-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="show">Horse Show</SelectItem>
                  <SelectItem value="clinic">Clinic</SelectItem>
                  <SelectItem value="meetup">Meetup</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe the event..."
                rows={3}
                className="bg-stone-50"
                data-testid="event-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="bg-stone-50"
                  data-testid="event-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="time">Time *</Label>
                <Input
                  id="time"
                  name="time"
                  type="time"
                  value={formData.time}
                  onChange={handleChange}
                  className="bg-stone-50"
                  data-testid="event-time-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location *</Label>
              <Input
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Main Arena"
                className="bg-stone-50"
                data-testid="event-location-input"
              />
            </div>

            {isPresident && (
              <div className="space-y-2">
                <Label htmlFor="registration_deadline">Close Entries At (optional)</Label>
                <Input
                  id="registration_deadline"
                  name="registration_deadline"
                  type="datetime-local"
                  value={formData.registration_deadline}
                  onChange={handleChange}
                  className="bg-stone-50"
                  data-testid="event-registration-deadline-input"
                />
                <p className="text-xs text-stone-500">After this date & time, members can no longer enter this event. Leave blank to keep entries open.</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">
                  {formData.event_type === 'show' ? 'Price per Class ($) *' : 'Price ($) *'}
                </Label>
                <Input
                  id="price"
                  name="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder={formData.event_type === 'show' ? '5.00' : '50.00'}
                  className="bg-stone-50"
                  data-testid="event-price-input"
                />
                {formData.event_type === 'show' && (
                  <p className="text-xs text-stone-500">Price per class for this horse show</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_participants">Max Participants *</Label>
                <Input
                  id="max_participants"
                  name="max_participants"
                  type="number"
                  min="1"
                  value={formData.max_participants}
                  onChange={handleChange}
                  placeholder="30"
                  className="bg-stone-50"
                  data-testid="event-max-participants-input"
                />
              </div>
            </div>

            {formData.event_type === 'show' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="show_classes">Show Classes (comma-separated)</Label>
                  <Textarea
                    id="show_classes"
                    name="show_classes"
                    value={formData.show_classes}
                    onChange={handleChange}
                    placeholder="Western Pleasure, Trail, Showmanship, Barrel Racing"
                    className="bg-stone-50 min-h-[80px]"
                    data-testid="event-classes-input"
                  />
                  <p className="text-xs text-stone-500">
                    {editingEvent 
                      ? 'Edit class names above. Each class costs $5 CAD to enter.'
                      : 'Leave blank to use default classes (Western Pleasure, Trail, etc.)'}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="age_categories">Age Categories (comma-separated)</Label>
                  <Textarea
                    id="age_categories"
                    name="age_categories"
                    value={formData.age_categories}
                    onChange={handleChange}
                    placeholder="Youth (7-12), Junior (13-17), Adult (18-49), Senior (50+)"
                    className="bg-stone-50 min-h-[60px]"
                    data-testid="event-age-categories-input"
                  />
                  <p className="text-xs text-stone-500">
                    {editingEvent 
                      ? 'Edit age category names above.'
                      : 'Leave blank to use default age categories'}
                  </p>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="image_url">Image URL (optional)</Label>
              <Input
                id="image_url"
                name="image_url"
                value={formData.image_url}
                onChange={handleChange}
                placeholder="https://..."
                className="bg-stone-50"
                data-testid="event-image-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => { setShowEventModal(false); resetForm(); }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-primary hover:bg-primary/90"
                disabled={submitting}
                data-testid="event-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingEvent ? 'Update Event' : 'Create Event'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Registrations Modal */}
      <Dialog open={showRegistrationsModal} onOpenChange={setShowRegistrationsModal}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Registrations for {selectedEvent?.title}
            </DialogTitle>
            <DialogDescription>
              {eventRegistrations.length} registration(s) {selectedEvent?.event_type === 'show' && '• $5 CAD per class'}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            {eventRegistrations.length === 0 ? (
              <p className="text-center text-stone-500 py-8">No registrations yet.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Participant</TableHead>
                    <TableHead>Horse</TableHead>
                    <TableHead>Phone</TableHead>
                    {selectedEvent?.event_type === 'show' && (
                      <>
                        <TableHead>Age Category</TableHead>
                        <TableHead>Classes</TableHead>
                      </>
                    )}
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {eventRegistrations.map((reg) => (
                    <TableRow key={reg.id}>
                      <TableCell className="font-medium">{reg.participant_name}</TableCell>
                      <TableCell>{reg.horse_name}</TableCell>
                      <TableCell className="font-mono text-sm">{reg.phone_number}</TableCell>
                      {selectedEvent?.event_type === 'show' && (
                        <>
                          <TableCell className="text-sm">{reg.age_category || '-'}</TableCell>
                          <TableCell className="text-sm max-w-32">
                            {reg.selected_classes?.length > 0 ? (
                              <span title={reg.selected_classes.join(', ')}>
                                {reg.selected_classes.length} class{reg.selected_classes.length !== 1 ? 'es' : ''}
                              </span>
                            ) : '-'}
                          </TableCell>
                        </>
                      )}
                      <TableCell className="font-mono">${(reg.total_price || 0).toFixed(2)}</TableCell>
                      <TableCell>{getStatusBadge(reg.payment_status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {(reg.payment_status === 'pending' || reg.payment_status === 'cash') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => handleMarkAsPaid(reg.id)}
                              data-testid={`mark-paid-${reg.id}`}
                              title="Mark as Paid"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditRegistrationModal(reg)}
                            data-testid={`edit-reg-${reg.id}`}
                            title="Edit Registration"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDeleteRegistration(reg.id)}
                            data-testid={`delete-reg-${reg.id}`}
                            title="Delete Registration"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Registration Modal (Admin) */}
      <Dialog open={showEditRegModal} onOpenChange={setShowEditRegModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Edit Registration</DialogTitle>
            <DialogDescription>
              Update registration details for {selectedEvent?.title}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reg_participant_name">Participant Name</Label>
              <Input
                id="reg_participant_name"
                value={regFormData.participant_name}
                onChange={(e) => setRegFormData(prev => ({ ...prev, participant_name: e.target.value }))}
                placeholder="Enter participant name"
                data-testid="admin-edit-participant-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reg_horse_name">Horse Name</Label>
              <Input
                id="reg_horse_name"
                value={regFormData.horse_name}
                onChange={(e) => setRegFormData(prev => ({ ...prev, horse_name: e.target.value }))}
                placeholder="Enter horse name"
                data-testid="admin-edit-horse-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reg_phone_number">Phone Number</Label>
              <Input
                id="reg_phone_number"
                value={regFormData.phone_number}
                onChange={(e) => setRegFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                placeholder="Enter phone number"
                data-testid="admin-edit-phone-number"
              />
            </div>

            {/* Show-specific fields */}
            {selectedEvent?.event_type === 'show' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="reg_age_category">Age Category</Label>
                  <Select
                    value={regFormData.age_category}
                    onValueChange={(value) => setRegFormData(prev => ({ ...prev, age_category: value }))}
                  >
                    <SelectTrigger data-testid="admin-edit-age-category">
                      <SelectValue placeholder="Select age category" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedEvent?.age_categories?.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label>Classes ($5.00 each)</Label>
                  <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto border border-stone-200 rounded-md p-3">
                    {selectedEvent?.show_classes?.map((className) => (
                      <div key={className} className="flex items-center space-x-2">
                        <Checkbox
                          id={`admin-class-${className}`}
                          checked={regFormData.selected_classes?.includes(className)}
                          onCheckedChange={() => handleRegClassToggle(className)}
                          data-testid={`admin-class-${className.toLowerCase().replace(/\s+/g, '-')}`}
                        />
                        <label
                          htmlFor={`admin-class-${className}`}
                          className="text-sm cursor-pointer"
                        >
                          {className}
                        </label>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-stone-500">
                    Selected: {regFormData.selected_classes?.length || 0} class(es) = 
                    <span className="font-semibold text-stone-700"> ${((regFormData.selected_classes?.length || 0) * 5).toFixed(2)} CAD</span>
                  </p>
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowEditRegModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveRegistration}
              disabled={submitting}
              className="bg-primary hover:bg-primary/90"
              data-testid="admin-save-registration-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Homepage Modal */}
      <Dialog open={showHomepageModal} onOpenChange={setShowHomepageModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Edit Homepage Content</DialogTitle>
            <DialogDescription>
              {isPresident 
                ? 'Changes will be applied immediately.'
                : 'Changes will be submitted for president approval.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="hero_title">Hero Title</Label>
              <Input
                id="hero_title"
                name="hero_title"
                value={homepageForm.hero_title}
                onChange={handleHomepageChange}
                placeholder="Where Champions Begin Their Journey"
                className="bg-stone-50"
                data-testid="homepage-hero-title"
              />
              <p className="text-xs text-stone-500">Use line break to split into two lines</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="hero_subtitle">Hero Subtitle</Label>
              <Textarea
                id="hero_subtitle"
                name="hero_subtitle"
                value={homepageForm.hero_subtitle}
                onChange={handleHomepageChange}
                rows={2}
                className="bg-stone-50"
                data-testid="homepage-hero-subtitle"
              />
            </div>

            <div className="space-y-2">
              <ImageUploader
                label="Hero Background Image"
                value={homepageForm.hero_image}
                onChange={(url) => setHomepageForm({ ...homepageForm, hero_image: url })}
                token={token}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="about_title">About Section Title</Label>
              <Input
                id="about_title"
                name="about_title"
                value={homepageForm.about_title}
                onChange={handleHomepageChange}
                className="bg-stone-50"
                data-testid="homepage-about-title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="about_text">About Section Text</Label>
              <Textarea
                id="about_text"
                name="about_text"
                value={homepageForm.about_text}
                onChange={handleHomepageChange}
                rows={4}
                className="bg-stone-50"
                data-testid="homepage-about-text"
              />
            </div>

            <div className="space-y-2">
              <ImageUploader
                label="About Section Image"
                value={homepageForm.about_image}
                onChange={(url) => setHomepageForm({ ...homepageForm, about_image: url })}
                token={token}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cta_title">CTA Section Title</Label>
              <Input
                id="cta_title"
                name="cta_title"
                value={homepageForm.cta_title}
                onChange={handleHomepageChange}
                className="bg-stone-50"
                data-testid="homepage-cta-title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cta_text">CTA Section Text</Label>
              <Textarea
                id="cta_text"
                name="cta_text"
                value={homepageForm.cta_text}
                onChange={handleHomepageChange}
                rows={2}
                className="bg-stone-50"
                data-testid="homepage-cta-text"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowHomepageModal(false)}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-primary hover:bg-primary/90"
                onClick={handleSaveHomepage}
                disabled={submitting}
                data-testid="homepage-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : isPresident ? (
                  'Save Changes'
                ) : (
                  'Submit for Approval'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Master List Modal */}
      <Dialog open={showPrintModal} onOpenChange={setShowPrintModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Printer className="w-6 h-6" />
              Print Master List
            </DialogTitle>
            <DialogDescription>
              Preview and print the registration list for {selectedEvent?.title}
            </DialogDescription>
          </DialogHeader>
          
          {/* View Toggle & Filter Controls */}
          {selectedEvent?.event_type === 'show' && (
            <div className="space-y-3">
              {/* View Toggle */}
              <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg border border-primary/20">
                <Label className="text-sm font-medium">View:</Label>
                <div className="flex gap-1">
                  <Button
                    variant={printView === 'table' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPrintView('table')}
                    className={printView === 'table' ? 'bg-primary' : ''}
                    data-testid="view-table-btn"
                  >
                    Table View
                  </Button>
                  <Button
                    variant={printView === 'byAge' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPrintView('byAge')}
                    className={printView === 'byAge' ? 'bg-primary' : ''}
                    data-testid="view-by-age-btn"
                  >
                    By Age Group & Class
                  </Button>
                  <Button
                    variant={printView === 'byClass' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPrintView('byClass')}
                    className={printView === 'byClass' ? 'bg-primary' : ''}
                    data-testid="view-by-class-btn"
                  >
                    By Class & Age Group
                  </Button>
                </div>
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-4 p-4 bg-stone-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium whitespace-nowrap">Age Category:</Label>
                  <Select 
                    value={printFilter.ageCategory} 
                    onValueChange={(v) => setPrintFilter(prev => ({ ...prev, ageCategory: v }))}
                  >
                    <SelectTrigger className="w-48 bg-white" data-testid="filter-age-category">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {getUniqueAgeCategories().map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium whitespace-nowrap">Payment Status:</Label>
                  <Select 
                    value={printFilter.paymentStatus} 
                    onValueChange={(v) => setPrintFilter(prev => ({ ...prev, paymentStatus: v }))}
                  >
                    <SelectTrigger className="w-40 bg-white" data-testid="filter-payment-status">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(printFilter.ageCategory !== 'all' || printFilter.paymentStatus !== 'all') && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setPrintFilter({ ageCategory: 'all', paymentStatus: 'all' })}
                    className="text-stone-500"
                  >
                    Clear Filters
                  </Button>
                )}
                <div className="ml-auto text-sm text-stone-500">
                  Showing {filteredRegistrations.length} of {eventRegistrations.length} registrations
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 mb-4">
            <Button
              variant="outline"
              onClick={() => setShowPrintModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePrint}
              className="bg-primary hover:bg-primary/90"
              data-testid="print-master-list-btn"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>

          {/* Printable Content */}
          <div id="print-master-list" className="border rounded-lg p-6 bg-white">
            <div className="header text-center mb-6 border-b-2 border-primary pb-4">
              <h1 className="text-2xl font-bold text-primary">Manitoulin Horse Enthusiasts Club</h1>
              <h2 className="text-lg mt-1">{selectedEvent?.title}</h2>
              <div className="event-info text-sm text-stone-500 mt-2">
                <p>Date: {selectedEvent?.date ? formatEventDate(selectedEvent.date, { weekday: 'long' }) : ''}</p>
                <p>Location: {selectedEvent?.location}</p>
                {selectedEvent?.event_type === 'show' && <p>Entry Fee: $5.00 CAD per class</p>}
                {(printFilter.ageCategory !== 'all' || printFilter.paymentStatus !== 'all') && (
                  <p className="mt-1 font-medium text-primary">
                    Filtered: {printFilter.ageCategory !== 'all' ? `Age: ${printFilter.ageCategory}` : ''}
                    {printFilter.ageCategory !== 'all' && printFilter.paymentStatus !== 'all' ? ' | ' : ''}
                    {printFilter.paymentStatus !== 'all' ? `Status: ${printFilter.paymentStatus}` : ''}
                  </p>
                )}
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6 p-4 bg-stone-50 rounded-lg">
              <div className="text-center">
                <p className="text-xs text-stone-500">Total Entries</p>
                <p className="text-xl font-bold text-primary">{filteredRegistrations.length}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-stone-500">Paid</p>
                <p className="text-xl font-bold text-green-600">
                  {filteredRegistrations.filter(r => r.payment_status === 'paid').length}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-stone-500">Cash</p>
                <p className="text-xl font-bold text-blue-600">
                  {filteredRegistrations.filter(r => r.payment_status === 'cash').length}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-stone-500">Pending</p>
                <p className="text-xl font-bold text-amber-600">
                  {filteredRegistrations.filter(r => r.payment_status === 'pending').length}
                </p>
              </div>
            </div>

            {filteredRegistrations.length === 0 ? (
              <p className="text-center text-stone-500 py-8">
                {eventRegistrations.length === 0 
                  ? 'No registrations for this event.' 
                  : 'No registrations match the selected filters.'}
              </p>
            ) : printView === 'table' ? (
              /* Table View */
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="border border-stone-300 p-2 text-left">#</th>
                    <th className="border border-stone-300 p-2 text-left">Participant</th>
                    <th className="border border-stone-300 p-2 text-left">Horse</th>
                    <th className="border border-stone-300 p-2 text-left">Phone</th>
                    {selectedEvent?.event_type === 'show' && (
                      <>
                        <th className="border border-stone-300 p-2 text-left">Age Category</th>
                        <th className="border border-stone-300 p-2 text-left">Classes</th>
                      </>
                    )}
                    <th className="border border-stone-300 p-2 text-left">Total</th>
                    <th className="border border-stone-300 p-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {flattenedRegistrations.map((entry, index) => (
                    <tr key={`${entry.id}-${entry.horse_name}-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                      <td className="border border-stone-300 p-2">{index + 1}</td>
                      <td className="border border-stone-300 p-2 font-medium">{entry.participant_name}</td>
                      <td className="border border-stone-300 p-2">{entry.horse_name || '-'}</td>
                      <td className="border border-stone-300 p-2 font-mono text-xs">{entry.phone_number}</td>
                      {selectedEvent?.event_type === 'show' && (
                        <>
                          <td className="border border-stone-300 p-2 text-xs">{entry.age_category || '-'}</td>
                          <td className="border border-stone-300 p-2 text-xs max-w-[200px]">
                            {entry.horse_classes?.length > 0 
                              ? entry.horse_classes.join(', ')
                              : '-'}
                          </td>
                        </>
                      )}
                      <td className="border border-stone-300 p-2 font-mono">${(entry.total_price || 0).toFixed(2)}</td>
                      <td className={`border border-stone-300 p-2 font-bold ${
                        entry.payment_status === 'paid' ? 'text-green-600' :
                        entry.payment_status === 'cash' ? 'text-blue-600' :
                        'text-amber-600'
                      }`}>
                        {getStatusText(entry.payment_status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-stone-100 font-bold">
                    <td colSpan={selectedEvent?.event_type === 'show' ? 6 : 4} className="border border-stone-300 p-2 text-right">
                      Total Revenue:
                    </td>
                    <td className="border border-stone-300 p-2 font-mono">
                      ${filteredRegistrations.reduce((sum, r) => sum + (r.total_price || 0), 0).toFixed(2)}
                    </td>
                    <td className="border border-stone-300 p-2"></td>
                  </tr>
                </tfoot>
              </table>
            ) : printView === 'byAge' ? (
              /* By Age Group & Class View */
              <div className="space-y-6">
                {Object.keys(registrationsByAge).filter(age => Object.keys(registrationsByAge[age]).length > 0).map(ageCategory => {
                  const ageData = registrationsByAge[ageCategory];
                  const classes = Object.keys(ageData);
                  const totalInAge = classes.reduce((sum, cls) => sum + ageData[cls].length, 0);
                  
                  return (
                    <div key={ageCategory} className="border border-stone-300 rounded-lg overflow-hidden page-break-inside-avoid">
                      <div className="bg-primary text-white p-3">
                        <h3 className="font-bold text-lg">{ageCategory}</h3>
                        <p className="text-sm opacity-90">{totalInAge} entr{totalInAge !== 1 ? 'ies' : 'y'} in this age group</p>
                      </div>
                      
                      {classes.map(className => {
                        const riders = ageData[className];
                        return (
                          <div key={className} className="border-t border-stone-200">
                            <div className="bg-stone-100 px-3 py-2 font-semibold text-stone-700 flex justify-between items-center">
                              <span>{className}</span>
                              <span className="text-sm font-normal text-stone-500">{riders.length} entr{riders.length !== 1 ? 'ies' : 'y'}</span>
                            </div>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-stone-50">
                                  <th className="p-2 text-left w-8">#</th>
                                  <th className="p-2 text-left">Rider</th>
                                  <th className="p-2 text-left">Horse</th>
                                  <th className="p-2 text-left">Age Category</th>
                                  <th className="p-2 text-left">Phone</th>
                                  <th className="p-2 text-left w-20">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {riders.map((entry, idx) => (
                                  <tr key={`${entry.id}-${entry.horse_name}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                                    <td className="p-2 border-t border-stone-100">{idx + 1}</td>
                                    <td className="p-2 border-t border-stone-100 font-medium">{entry.participant_name}</td>
                                    <td className="p-2 border-t border-stone-100">{entry.horse_name}</td>
                                    <td className="p-2 border-t border-stone-100 text-xs">{entry.age_category || '-'}</td>
                                    <td className="p-2 border-t border-stone-100 font-mono text-xs">{entry.phone_number}</td>
                                    <td className={`p-2 border-t border-stone-100 font-bold text-xs ${
                                      entry.payment_status === 'paid' ? 'text-green-600' :
                                      entry.payment_status === 'cash' ? 'text-blue-600' :
                                      'text-amber-600'
                                    }`}>
                                      {getStatusText(entry.payment_status)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                
                {/* Summary by Age Group */}
                <div className="bg-stone-100 p-4 rounded-lg mt-4">
                  <h4 className="font-bold text-stone-700 mb-2">Summary by Age Group</h4>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-300">
                        <th className="p-2 text-left">Age Group</th>
                        <th className="p-2 text-center">Riders</th>
                        <th className="p-2 text-center">Classes Entered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(registrationsByAge).filter(age => Object.keys(registrationsByAge[age]).length > 0).map(ageCategory => {
                        const ageData = registrationsByAge[ageCategory];
                        const totalRiders = Object.values(ageData).reduce((sum, riders) => sum + riders.length, 0);
                        const uniqueRiders = [...new Set(Object.values(ageData).flat().map(r => r.id))].length;
                        return (
                          <tr key={ageCategory} className="border-b border-stone-200">
                            <td className="p-2 font-medium">{ageCategory}</td>
                            <td className="p-2 text-center">{uniqueRiders}</td>
                            <td className="p-2 text-center">{totalRiders}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              /* By Class & Age Group View */
              <div className="space-y-6">
                {getEventClasses().map(className => {
                  const classData = registrationsByClass[className] || {};
                  const ageGroups = Object.keys(classData).sort();
                  const totalInClass = ageGroups.reduce((sum, ag) => sum + classData[ag].length, 0);
                  
                  if (totalInClass === 0) return null;
                  
                  return (
                    <div key={className} className="border border-stone-300 rounded-lg overflow-hidden page-break-inside-avoid">
                      <div className="bg-primary text-white p-3">
                        <h3 className="font-bold text-lg">{className}</h3>
                        <p className="text-sm opacity-90">{totalInClass} entr{totalInClass !== 1 ? 'ies' : 'y'}</p>
                      </div>
                      
                      {ageGroups.map(ageGroup => {
                        const riders = classData[ageGroup];
                        return (
                          <div key={ageGroup} className="border-t border-stone-200">
                            <div className="bg-stone-100 px-3 py-2 font-semibold text-stone-700 flex justify-between items-center">
                              <span>{ageGroup}</span>
                              <span className="text-sm font-normal text-stone-500">{riders.length} entr{riders.length !== 1 ? 'ies' : 'y'}</span>
                            </div>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-stone-50">
                                  <th className="p-2 text-left w-8">#</th>
                                  <th className="p-2 text-left">Rider</th>
                                  <th className="p-2 text-left">Horse</th>
                                  <th className="p-2 text-left">Age Category</th>
                                  <th className="p-2 text-left">Phone</th>
                                  <th className="p-2 text-left w-20">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {riders.map((entry, idx) => (
                                  <tr key={`${entry.id}-${entry.horse_name}-${idx}`} className={idx % 2 === 0 ? 'bg-white' : 'bg-stone-50'}>
                                    <td className="p-2 border-t border-stone-100">{idx + 1}</td>
                                    <td className="p-2 border-t border-stone-100 font-medium">{entry.participant_name}</td>
                                    <td className="p-2 border-t border-stone-100">{entry.horse_name}</td>
                                    <td className="p-2 border-t border-stone-100 text-xs">{entry.age_category || '-'}</td>
                                    <td className="p-2 border-t border-stone-100 font-mono text-xs">{entry.phone_number}</td>
                                    <td className={`p-2 border-t border-stone-100 font-bold text-xs ${
                                      entry.payment_status === 'paid' ? 'text-green-600' :
                                      entry.payment_status === 'cash' ? 'text-blue-600' :
                                      'text-amber-600'
                                    }`}>
                                      {getStatusText(entry.payment_status)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
                
                {/* Summary */}
                <div className="bg-stone-100 p-4 rounded-lg mt-4">
                  <h4 className="font-bold text-stone-700 mb-2">Summary by Class</h4>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-300">
                        <th className="p-2 text-left">Class</th>
                        <th className="p-2 text-center">Total Riders</th>
                        <th className="p-2 text-center">Paid</th>
                        <th className="p-2 text-center">Cash</th>
                        <th className="p-2 text-center">Pending</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getEventClasses().map(className => {
                        const classData = registrationsByClass[className] || {};
                        const allRiders = Object.values(classData).flat();
                        if (allRiders.length === 0) return null;
                        return (
                          <tr key={className} className="border-b border-stone-200">
                            <td className="p-2 font-medium">{className}</td>
                            <td className="p-2 text-center">{allRiders.length}</td>
                            <td className="p-2 text-center text-green-600">{allRiders.filter(r => r.payment_status === 'paid').length}</td>
                            <td className="p-2 text-center text-blue-600">{allRiders.filter(r => r.payment_status === 'cash').length}</td>
                            <td className="p-2 text-center text-amber-600">{allRiders.filter(r => r.payment_status === 'pending').length}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="footer mt-6 text-center text-xs text-stone-400 border-t pt-4">
              <p>Generated on {new Date().toLocaleString()}</p>
              <p>Manitoulin Horse Enthusiasts Club - Master Registration List</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default DashboardPage;
